import "../../../styles/housing/HousingAllocations.css"
import myStyles from "../../../styles/housing/HousingAllocations.module.css"
import { useCallback, useState, useContext, useEffect } from "react"
import { UserContext } from "../../../context/userContext"
import { housingApiListHousesPost, housingApiGetHouseGet } from "../housingApi"
import { LogError } from "../../../helpers/logger"
import { housingApiUrl } from "../../../config/microservicesUrls"
import defaultImage from "../../../assets/images/default-profile-avatar.jpg"
import OccupantForm from "./OccupantForm"
import { HouseSelection } from "./HouseSelection"
import { RoomSelection } from "./RoomSelection"
import { ActiveRoom } from "./ActiveRoom"
import { LoaderMedium } from "../../../components/Loaders"

export default function HousingAllocations({
  currentParticipant,
  systemAllParticipants,
  systemActiveParticipants,
  systemPrograms,
  onNewAllocationCreated,
  isLoadingPrograms,
}) {
  const userCtx = useContext(UserContext)

  const [isLoadingHouseList, setIsLoadingHouseList] = useState(true)
  const [isLoadingHouseDetail, setIsLoadingHouseDetail] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [houses, setHouses] = useState(null)
  const [selectedHouse, setSelectedHouse] = useState(null)
  const [rooms, setRooms] = useState(null)
  const [selectedRoom, setSelectedRoom] = useState(null)
  const [beds, setBeds] = useState(null)
  const [selectedBed, setSelectedBed] = useState(null)
  const [bedOccupants, setBedOccupants] = useState(null)
  const [currentBedAllocation, setCurrentBedAllocation] = useState(undefined)

  const getHouses = useCallback(async () => {
    try {
      setIsLoadingHouseList(true)
      let houseList = await housingApiListHousesPost(
        userCtx.tokenHousing,
        userCtx.session.user.enterprise_id,
        userCtx.session.user.enterprise_id ===
          userCtx.session.user.organization_id
          ? "all"
          : userCtx.session.user.organization_id,
        1000,
        1,
        ""
      )

      if (houseList.houses.length > 0) {
        const url = housingApiUrl()
        houseList.houses.forEach((house) => {
          house.house.shortAddress =
            `${house.house.address}${
              !!house.house.city ? `;${house.house.city}` : ""
            }${!!house.house.state ? `;${house.house.state}` : ""}` || ""

          house.house.relativeImage = !!house.house.image
            ? `${url}/${house.house.image}`
                .replaceAll('"', "")
                .replaceAll("\\", "/")
            : defaultImage
        })

        if (userCtx.housing?.context?.houseId) {
          var defaultHouse = houseList?.houses?.find(
            (house) => house?.house.id === userCtx.housing?.context?.houseId
          )
          if (defaultHouse) {
            setSelectedHouse(defaultHouse)
            userCtx.housing?.setHousing({
              ...userCtx.housing?.context,
              houseId: undefined,
            })
          } else {
            setSelectedHouse(houseList.houses[0])
          }
        } else {
          setSelectedHouse(houseList.houses[0])
        }
      }

      setHouses(houseList)

      setIsLoadingHouseList(false)
    } catch (error) {
      LogError("Unable to retrieve Housing listing", error)
      userCtx.setNotificationError("Unable to retrieve Housing listing")
      setHouses(undefined)
      setIsLoadingHouseList(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userCtx.session.user.enterprise_id,
    userCtx.session.user.organization_id,
    userCtx.tokenHousing,
  ])

  useEffect(() => {
    getHouses()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getHouse = useCallback(async () => {
    try {
      if (selectedHouse) {
        if (!!selectedHouse?.house.id) {
          setIsLoadingHouseDetail(true)

          let result = await housingApiGetHouseGet(
            userCtx.tokenHousing,
            selectedHouse?.house.id
          )

          if (!!result) {
            result.house.organizationIds = result.organizations.map(
              (organization) => organization.organizationId
            )

            result.beds.forEach((bed) => {
              bed.isAllocated = result.bedOccupations.some(
                (occupation) => occupation.bedId === bed.id
              )
            })

            let activeRoom = result.rooms[0]

            if (userCtx.housing?.context?.roomId) {
              const defaultRoom = result.rooms.find(
                (room) => room.id === userCtx.housing?.context?.roomId
              )

              if (defaultRoom) {
                activeRoom = defaultRoom
              }
              userCtx.housing?.setHousing({
                ...userCtx.housing?.context,
                roomId: undefined,
              })
            }

            setRooms(result.rooms)
            setSelectedRoom(activeRoom)
            setBeds(result.beds)
            setBedOccupants(result.bedOccupations)
            setIsLoadingHouseDetail(false)
          } else {
            throw new Error("House not found")
          }
        }
      }
    } catch (error) {
      LogError("Unable to retrieve House profile", error)
      userCtx.setNotificationError("Unable to retrieve house profile")
      setIsLoadingHouseDetail(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHouse, userCtx.tokenHousing])

  useEffect(() => {
    getHouse()
  }, [getHouse])

  const newAllocationCreated = () => {
    setShowForm(false)
    getHouse()
    onNewAllocationCreated()
  }

  const editAllocation = (bedOccupancy) => {
    setCurrentBedAllocation(bedOccupancy)
    setShowForm(true)
  }

  return (
    <>
      <div className={myStyles.houseMainGridContainer}>
        <div className={myStyles.houseMainGridHouse}>
          <h3>Houses</h3>
          <br />

          {isLoadingHouseList ? (
            <LoaderMedium />
          ) : (
            houses?.houses.map((x, index) => (
              <HouseSelection
                houseObject={x}
                clickAction={setSelectedHouse}
                selectedHouse={selectedHouse}
                key={index}
                programs={systemPrograms}
                isLoadingPrograms={isLoadingPrograms}
              />
            ))
          )}
        </div>
        <div className={myStyles.houseMainGridRoom}>
          <h3>Rooms</h3>
          <br />
          {isLoadingHouseDetail || isLoadingHouseList ? (
            <LoaderMedium />
          ) : (
            rooms?.map((x, index) => (
              <RoomSelection
                room={x}
                clickAction={setSelectedRoom}
                selectedRoom={selectedRoom}
                key={index}
              />
            ))
          )}
        </div>
        <div className={myStyles.houseMainGridDetail}>
          <h3>
            Available beds:{" "}
            {selectedRoom && !isLoadingHouseDetail && !isLoadingHouseList
              ? selectedRoom.name
              : ""}
          </h3>
          {isLoadingHouseDetail || isLoadingHouseList ? (
            <LoaderMedium />
          ) : (
            <ActiveRoom
              participants={systemAllParticipants}
              participantsLoading={systemAllParticipants === null}
              beds={beds}
              bedOccupants={bedOccupants}
              selectedRoom={selectedRoom}
              setSelectedBed={setSelectedBed}
              setShowForm={setShowForm}
              onEditStay={editAllocation}
            />
          )}
        </div>
      </div>
      <OccupantForm
        showForm={showForm}
        onHideForm={() => {
          setShowForm(false)
          getHouse()
        }}
        onOccupantAdded={newAllocationCreated}
        activeParticipants={systemActiveParticipants}
        allParticipants={systemAllParticipants}
        houseList={houses?.houses}
        roomList={rooms}
        bedList={beds}
        currentParticipant={currentParticipant}
        editBedAllocation={currentBedAllocation}
        selectedBed={
          selectedBed
            ? {
                ...selectedBed,
                rentValue: selectedHouse.house.rentValue,
                rentFrequency: selectedHouse.house.rentFrequency,
                rentFrequencyDetail: selectedHouse.house.rentFrequencyDetail,
                rentGraceDays: selectedHouse.house.rentGraceDays,
              }
            : {}
        }
      />
    </>
  )
}
